import { NavLink } from "react-router-dom";
import type { CSSProperties } from "react";

export type GuestStudioTab =
  | "inbox"
  | "crm"
  | "members"
  | "kits"
  | "pours"
  | "events"
  | "reviews"
  | "photos"
  | "trails"
  | "edits";

interface Props {
  activeTab: GuestStudioTab;
  badgeCounts?: {
    inbox?: number;
    crm?: number;
    members?: number;
    kits?: number;
    pours?: number;
    events?: number;
    reviews?: number;
    photos?: number;
    trails?: number;
    edits?: number;
  };
}

const TABS: {
  id: GuestStudioTab;
  to: string;
  label: string;
  countKey?: keyof NonNullable<Props["badgeCounts"]>;
}[] = [
  { id: "inbox", to: "/admin/communications", label: "💬 Unified Inbox", countKey: "inbox" },
  { id: "crm", to: "/admin/crm", label: "👥 Guest CRM", countKey: "crm" },
  { id: "members", to: "/admin/members", label: "👑 C33 Roster", countKey: "members" },
  { id: "kits", to: "/admin/welcome-kits", label: "🎁 Welcome Kits", countKey: "kits" },
  { id: "pours", to: "/admin/club-pours", label: "🥃 Club Pour Planner", countKey: "pours" },
  { id: "events", to: "/admin/events", label: "🎟️ Events & Check-In", countKey: "events" },
  { id: "reviews", to: "/admin/reviews", label: "⭐ Reviews & AI", countKey: "reviews" },
  { id: "photos", to: "/admin/bottle-photos", label: "📸 Bottle Photos", countKey: "photos" },
  { id: "trails", to: "/admin/trails", label: "🗺️ Tasting Trails", countKey: "trails" },
  { id: "edits", to: "/admin/suggested-edits", label: "✍️ Suggested Edits", countKey: "edits" },
];

export default function GuestStudioTabs({ activeTab, badgeCounts }: Props) {
  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.badge}>Guests &amp; C33 Community Studio</div>
          <h1 style={styles.title}>Guest Experience &amp; C33 VIP Studio</h1>
          <p style={styles.subtitle}>
            Manage member communications, VIP club allocations, event check-ins, Google review responses, and community suggestions.
          </p>
        </div>
      </div>

      <nav style={styles.tabNav} aria-label="Guests and Community Studio Tabs">
        {TABS.map((tab) => {
          const isActive = tab.id === activeTab;
          const count = tab.countKey ? badgeCounts?.[tab.countKey] : undefined;
          return (
            <NavLink
              key={tab.id}
              to={tab.to}
              style={{
                ...styles.tabLink,
                ...(isActive ? styles.tabLinkActive : {}),
              }}
            >
              <span>{tab.label}</span>
              {typeof count === "number" && (
                <span style={isActive ? styles.countActive : styles.countInactive}>
                  {count}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    marginBottom: 20,
    borderBottom: "1px solid var(--border)",
    paddingBottom: 12,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  badge: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#C084FC",
    background: "rgba(192, 132, 252, 0.12)",
    padding: "3px 8px",
    borderRadius: 4,
    marginBottom: 6,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "var(--text)",
    letterSpacing: "-0.01em",
  },
  subtitle: {
    margin: "4px 0 0 0",
    fontSize: 13,
    color: "var(--muted)",
  },
  tabNav: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    paddingBottom: 4,
  },
  tabLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: "var(--muted)",
    textDecoration: "none",
    background: "rgba(255, 255, 255, 0.02)",
    border: "1px solid var(--border)",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
  },
  tabLinkActive: {
    color: "#0D0B08",
    background: "var(--gold)",
    borderColor: "var(--gold)",
    fontWeight: 700,
  },
  countActive: {
    fontSize: 11,
    background: "rgba(0,0,0,0.18)",
    color: "#0D0B08",
    padding: "1px 6px",
    borderRadius: 10,
    fontWeight: 800,
  },
  countInactive: {
    fontSize: 11,
    background: "rgba(255,255,255,0.06)",
    color: "var(--muted)",
    padding: "1px 6px",
    borderRadius: 10,
    fontWeight: 600,
  },
};
